import React, { FC, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';

import CloseIconButton from 'Src/components/buttons/CloseIconButton';
import { IconCTA } from 'Src/components/buttons/IconCTA';
import { StackedIconCTA } from 'Src/components/buttons/StackedIconCTA';
import Copy from 'Src/components/Copy';
import FontIcon from 'Src/components/icons/FontIcon';
import { Flex } from 'Src/components/layout/Flex';
import { CheckPanelFooterMainCTA } from 'Src/feature/check/CheckPanelFooterMainCTA';
import { CheckItemEditActions } from 'Src/feature/check-item/CheckItemEditActions';
import { UsedCurrentCheckItemButtons } from 'Src/feature/check-item/useCurrentCheckItemButtons';
import { CheckTransactions } from 'Src/feature/check-transaction/CheckTransactions';
import { GiftCardAddToCheckModal } from 'Src/feature/gift-card/GiftCardAddToCheckModal';
import { useResizeObserver } from 'Src/feature/resize/useResizeObserver';
import { useTerminal } from 'Src/feature/terminal/useTerminal';

import { CheckPanelFooterModals } from './CheckPanelFooterModals';
import { CheckTotalEntry } from './CheckTotalEntry';
import { Check } from './models';
import { useCheckPanelFooter } from './useCheckPanelFooter';

import styles from './CheckPanelFooter.module.scss';

interface CheckPanelFooterProps {
  onGoBack: () => void;
  checkItemButtons: UsedCurrentCheckItemButtons;
}

const CheckPanelFooter: FC<CheckPanelFooterProps> = (props) => {
  const { t } = useTranslation();
  const store = useCheckPanelFooter();
  const rootRef = useRef<HTMLDivElement>(null);
  const { check } = store.described;
  const currentFiscalDate = useTerminal.query.fiscalDate();

  const updateRootHeight = () => {
    // const targetElement = targetRef.current;
    if (!rootRef.current) return;
    if (store.viewMode.is.main) {
      if (!resizeDetail.element) return;
      rootRef.current.style.height = `${resizeDetail.element.offsetHeight || 0}px`;

    } else if (store.viewMode.is.overlay) {
      if (!resizeOverlay.element) return;
      rootRef.current.style.height = `${resizeOverlay.element.offsetHeight || 0}px`;
    }
  };

  const resizeDetail = useResizeObserver({
    // safe to omit debounce as state is not getting mutated
    debounceMillis: 0,
    onResize: updateRootHeight
  });
  const resizeOverlay = useResizeObserver({
    // safe to omit debounce as state is not getting mutated
    debounceMillis: 0,
    onResize: updateRootHeight
  });

  return (
    <>
      <div
        ref={rootRef}
        className={classNames(styles.root, {
          [styles.viewModeMain]: store.viewMode.is.main,
          [styles.viewModeOverlay]: store.viewMode.is.overlay,
        })}
      >
        <div
          ref={resizeDetail.setElement}
          className={styles.checkDetails}
        >
          {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events  */}
          <div
            className={styles.handle}
            onClick={() => {
              if (store.viewMode.active === 'CheckDetailsExpanded') {
                store.viewMode.update('CheckDetails');
              } else {
                store.viewMode.update('CheckDetailsExpanded');
              }
            }}
          />
          {store.viewMode.active === 'CheckDetailsExpanded' ? (
            <>
              {(check?.info?.discounts || []).some((d) => d.status === 'Enabled') && (
                <div className={styles.discountsWrapper}>
                  {(check?.info?.discounts || [])
                    .filter((d) => d.status === 'Enabled')
                    .map((discount) => {
                      const discountText = discount.name;
                      const isPercent = discount.type === 'Percent';
                      const calculatedValue = isPercent
                        ? ((check?.balance?.subtotal || 0) * (discount.value / 100))
                        : discount.value;
                      const formattedAmount = t('labels.util.currencyNegative', { value: calculatedValue.toFixed(2) });

                      return (
                        <div key={discount.id} className={styles.discountCardContainer}>
                          <Flex
                            horizontal
                            alignItems="center"
                            gap="sm"
                            className={styles.discount_item}
                            onClick={() => {
                              store.modal.discount.open();
                            }}
                          >
                            <FontIcon name="sell" size="sm" className={styles.discount_info_icon} />
                            <div className={styles.discount_label}>
                              <Copy variant="body" weight="bold" value={discountText} />
                              {discount.note && <Copy variant="caption" color="grey-200" value={discount.note} />}
                            </div>
                            <Copy variant="body" value={formattedAmount} className={styles.discount_calculated} />
                          </Flex>
                        </div>
                      );
                    })}
                </div>
              )}
              <div className={styles.total}>
                <CheckTotalEntry type="subtotal" check={check} />
                <CheckTotalEntry type="fees" check={check} />
                <CheckTotalEntry type="gratuities" check={check} />
                <CheckTotalEntry type="discount" check={check} />
                <CheckTotalEntry type="tax" check={check} />
                <CheckTotalEntry type="total" check={check} />
                <CheckTotalEntry type={store.described.has.check.balance.overpaid ? 'overpaid' : 'balance'} check={check} />
              </div>
            </>
          ) : (
            <div className={styles.total}>
              <CheckTotalEntry type="total" check={check} />
              <CheckTotalEntry type={store.described.has.check.balance.overpaid ? 'overpaid' : 'balance'} check={check} />
            </div>
          )}

          <CheckTransactions />

          {check?.status !== 'Closed' && !['Voided', 'MergeVoided'].includes(check?.status ?? '') && (
            <div className={styles.actions}>
              <CheckPanelFooterMainCTA store={store} />
              <IconCTA
                icon="more_vert"
                color="tertiary"
                size="lg"
                onClick={() => {
                  store.viewMode.update('CheckDetailsMore');
                }}
              />
            </div>
          )}
        </div>

        <div className={styles.overlayContainer}>
          <div ref={resizeOverlay.setElement} className={styles.overlayContent}>
            {
              store.viewMode.active === 'CheckDetailsMore' && (
                <div className={styles.checkDetailsMore}>
                  <div className={styles.checkDetailsMoreGrid}>
                    {store.can.takePayment && (
                      <StackedIconCTA
                        size="sm"
                        padding="sm"
                        icon="payment"
                        label={t('labels.pay')}
                        onClick={() => {
                          //send any unsent items
                          store.sendItems();
                          store.onPayClick();
                        }}
                        disabled={check?.status === 'Scheduled' && currentFiscalDate.data !== check.fiscalDate}
                      />
                    )}
                    {(!store.described.unsent.check.itemIds.length) && (
                      <StackedIconCTA
                        size="sm"
                        padding="sm"
                        icon="print"
                        label={t('labels.print')}
                        onClick={store.onPrintClick}
                      />
                    )}
                    {store.can.manageFees && (
                      <StackedIconCTA
                        size="sm"
                        padding="sm"
                        icon="attach_money"
                        label={t('labels.fees')}
                        onClick={store.modal.fees.open}
                      />
                    )}
                    {store.can.mergeChecks && (
                      <StackedIconCTA
                        size="sm"
                        padding="sm"
                        icon="merge"
                        label={t('labels.merge')}
                        onClick={store.modal.mergeCheck.open}
                        disabled={check?.status === 'Scheduled'}
                      />
                    )}
                    {store.can.splitCheck && (
                      <StackedIconCTA
                        size="sm"
                        padding="sm"
                        icon="call_split"
                        label={t('checkSplit.labels.split')}
                        onClick={store.onSplitClick}
                        disabled={check?.status === 'Scheduled'}
                      />
                    )}
                    {store.can.transferCheck && (
                      <StackedIconCTA
                        size="sm"
                        padding="sm"
                        icon="arrow_forward"
                        label={t('labels.transfer')}
                        onClick={store.onTransferClick}
                        disabled={check?.status === 'Scheduled'}
                      />
                    )}
                    {store.can.manageDiscounts &&
                      (
                        <StackedIconCTA
                          size="sm"
                          padding="sm"
                          icon="sell"
                          label={t('discount.label')}
                          onClick={store.modal.discount.open}
                        />
                      )
                    }
                    {store.can.taxExempt && (
                      <StackedIconCTA
                        size="sm"
                        padding="sm"
                        icon="payments"
                        label={t('labels.taxExempt')}
                        onClick={store.modal.taxExempt.open}
                      />
                    )}
                    {store.can.clearOrVoid && (
                      <StackedIconCTA
                        size="sm"
                        padding="sm"
                        icon="block"
                        label={store.described.can.clear ? t('labels.clear') : t('labels.void')}
                        onClick={store.described.can.clear ? store.modal.clearCheck.open : store.modal.voidCheck.open}
                      />
                    )}
                    {store.can.quickSend && (
                      <StackedIconCTA
                        size="sm"
                        padding="sm"
                        icon="send"
                        label={t('labels.send')}
                        onClick={() => {
                          store.sendItems();
                          store.viewMode.update('CheckDetails');
                        }}
                      />
                    )}
                    {
                      !store.isPaymentRoute && (
                        <GiftCardAddToCheckModal
                          // this component includes the button that controls the modal as well as the modal
                          check={check as Check}
                        />
                      )
                    }
                  </div>
                  <div className={styles.checkDetailsMoreCloseBtn}>
                    <CloseIconButton
                      onClick={() => {
                        store.viewMode.update('CheckDetails');
                      }}
                    />
                  </div>
                </div>
              )
            }
            {
              store.viewMode.active === 'CheckItemEdit' && (
                <CheckItemEditActions {...props} />
              )
            }
          </div>
        </div>
      </div>
      <CheckPanelFooterModals store={store} />
    </>
  );
};

export { CheckPanelFooter, CheckPanelFooterProps };
